/**
 * Generic children list for an umbrella parent (multi-item claim, etc.).
 * HOA parents use the richer ChildClaimsTab; this is the plain "items under
 * this claim" view - one row per child claim, click through to the child.
 *
 * When the caller has the parent list's column definitions in scope (the
 * claims list page), it passes them (and the user's visibility/order config)
 * so each item shows the same columns as the main table, indented ~32px to
 * signal the nesting. Callers with no parent list in scope - the claim detail
 * page's Items tab - omit `columns`; the list then falls back to a
 * self-contained default column set so the table still renders instead of
 * crashing on `undefined.filter(...)` inside DataTable.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { DataTable, type DataTableColumn } from "@/admin/components/data-table";
import { StatusBadge } from "@/admin/components/shared/status-badge";
import { useClaimChildren, type ClaimRow } from "@/admin/hooks/use-claims";
import { formatClaimId } from "@/admin/lib/formatting";
import { useFormatters } from "@/shared/lib/formatting";

export function UmbrellaChildrenList({
  parentUuid,
  columns,
  columnVisibility,
  columnOrder,
}: {
  parentUuid: string;
  columns?: DataTableColumn<ClaimRow>[];
  columnVisibility?: Record<string, boolean>;
  columnOrder?: string[];
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const {
    data: children = [],
    isLoading,
    isFetching,
  } = useClaimChildren(parentUuid);

  // Fallback column set for callers with no parent-list columns in scope (the
  // claim detail page's Items tab). Reads plain ClaimRow fields; the state
  // label + colour come from the row (workflow-driven via the API), so nothing
  // here is hardcoded per tenant.
  const defaultColumns: DataTableColumn<ClaimRow>[] = useMemo(
    () => [
      {
        id: "uuid",
        header: t("claims.columnClaimId", "Claim ID"),
        sortable: false,
        cell: (row) => (
          <span className="block truncate font-mono text-[12px] uppercase text-[var(--ink-2)]">
            {row.claim_number || formatClaimId(row.uuid)}
          </span>
        ),
      },
      {
        id: "product",
        header: t("claims.columnProduct", "Product"),
        sortable: false,
        cell: (row) => (
          <span className="block truncate text-[13px] text-[var(--ink-2)]">
            {row.product_name || "-"}
          </span>
        ),
      },
      {
        id: "consumer",
        header: t("claims.columnCustomer", "Customer"),
        sortable: false,
        cell: (row) => (
          <span className="block truncate text-[13px] text-[var(--ink)]">
            {row.consumer_account_name || row.consumer_account_email || "-"}
          </span>
        ),
      },
      {
        id: "state",
        header: t("claims.columnState", "State"),
        sortable: false,
        cell: (row) =>
          row.claim_state ? (
            <StatusBadge
              status={row.claim_state}
              color={row.claim_state_color}
            />
          ) : (
            <span className="text-[var(--ink-3)]">-</span>
          ),
      },
      {
        id: "created_at",
        header: t("claims.columnCreated", "Created"),
        sortable: false,
        cell: (row) => (
          <span className="text-[11px] text-[var(--ink-3)]">
            {formatDate(row.created_at)}
          </span>
        ),
      },
    ],
    [formatDate, t],
  );

  return (
    <div className="pl-8">
      <DataTable
        tableId="umbrella-children"
        columns={columns ?? defaultColumns}
        data={children}
        columnVisibility={columnVisibility}
        columnOrder={columnOrder}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey="uuid"
        onRowClick={(r) => navigate(`/claims/${r.uuid}?tab=overview`)}
        emptyMessage={t("claims.noItemsInClaim", "No items in this claim yet.")}
      />
    </div>
  );
}
